/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-dynamic-delete */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthenticationError } from "./data/base_classes/authentication_error";
import { auth } from "./firebase";
import { isFalsy } from "./utils";
import {getAuth, onAuthStateChanged} from "firebase/auth";

/**
 * Check if a field is validated
 * @param {string} key - The field key
 * @returns {boolean | null} True if the field is validated, false if not, null if not checked
 */
export function isValidated(
  errors: Array<{ field: string }>,
  key: string,
): boolean | null {
  return errors?.find(({ field }) => field === key) === undefined
    ? null
    : false;
}

/**
 * Find the user ID
 * @param {string} userId - The userId to check
 * @returns {string} Returns the userId or fails with an error
 */
export function validateOrGetUserId(userId?: string): string {
  if (isFalsy(userId)) {
    userId = auth.firebaseUser?.uid ?? undefined;
  }
  if (isFalsy(userId)) {
    userId = sessionStorage.getItem("userId") ?? undefined;
  }
  if (userId === "" || userId === undefined) {
    throw new AuthenticationError("No user ID found");
  }

  return userId;
}

/**
 * Check if a route patrh exists
 * @param {string} path - The path to check
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function routePathExists(routes: any[], path: string): boolean {
  // Remove query parameters
  const currentPath = path.split("?")[0];

  if (currentPath.includes("sign_out")) {
    return true;
  }

  if (
    currentPath.includes("loading") ||
    currentPath.includes("error") ||
    currentPath.includes("sign_in") ||
    currentPath.includes("sign_up") ||
    currentPath.includes("organisation/add")
  ) {
    return false;
  }

  // Check if the path exists
  const filteredRoutes = routes.filter((r) => {
    const p0 = r.path.split("/");
    const p1 = currentPath.split("/");
    let index = 0;
    let equal = true;
    while (p0[index] !== undefined && p1[index] !== undefined && equal) {
      if (p0[index] !== p1[index] && p0[index].includes(":") === false) {
        equal = false;
      }
      index++;
    }
    return equal;
  });

  return filteredRoutes.length > 0;
}

/**
 * Check if route path is active
 * @param {string} path - The path to check
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isActive(
  router: any,
  split: string,
  name: string | string[],
): boolean {
  if (name instanceof Array) {
    for (const n of name) {
      if (isActive(router, split, n)) {
        return true;
      }
    }
    return false;
  }
  return (
    router.currentRoute.value.fullPath
      .split("/" + split + "/")
      .pop()
      ?.startsWith(name) ?? false
  );
}

/**
 * Generate a 6-character random string of alpha-numeric characters
 * @returns {string} The random string
 */
export function generateVerificationCode(): string {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  const length = 6;
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    result += characters.charAt(randomIndex);
  }
  return result;
}

/**
 * Clean a JSON object from null, undefined or empty values
 */
export function cleanJSON(json: Record<string, any>): Record<string, unknown> {
  Object.keys(json).forEach((key) => {
    if (json[key] === null || json[key] === undefined || json[key] === "") {
      delete json[key];
    }

    if (Array.isArray(json[key]) && json[key].length === 0) {
      delete json[key];
    }

    if (typeof json[key] === "object" && Object.keys(json[key]).length === 0) {
      delete json[key];
    }

    if (typeof json[key] === "object" && Array.isArray(json[key])) {
      json[key] = json[key].map((item: any) => cleanJSON(item));
    }

    if (typeof json[key] === "object" && !Array.isArray(json[key])) {
      json[key] = cleanJSON(json[key]);
    }
  });

  return json;
}

export function createRandomString(length: number): string {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export async function getUserId(retryCount = 0, maxRetries = 3): Promise<string> {
  const auth = getAuth();

  // If user is already authenticated, return the ID immediately
  if (auth.currentUser?.uid) {
    return auth.currentUser.uid;
  }

  // Check if we've exceeded the maximum number of retries
  if (retryCount >= maxRetries) {
    console.error(`Failed to retrieve user ID after ${maxRetries} attempts`);
    throw new Error("Maximum retry attempts exceeded for user ID retrieval");
  }

  return new Promise((resolve, reject) => {
    // Timeout failsafe - increased to 10 seconds for the first attempt, shorter for retries
    const timeoutDuration = retryCount === 0 ? 10000 : 5000;
    const timeout = setTimeout(() => {
      unsubscribe();

      // Instead of rejecting, retry with an incremented retry count
      console.warn(`User ID retrieval timed out (attempt ${retryCount + 1}/${maxRetries + 1}), retrying...`);
      getUserId(retryCount + 1, maxRetries)
        .then(resolve)
        .catch(reject);
    }, timeoutDuration);

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user?.uid) {
        clearTimeout(timeout);
        unsubscribe(); // Stop listening after getting the user ID
        resolve(user.uid);
      } else if (user === null) {
        // User is definitely not logged in
        clearTimeout(timeout);
        unsubscribe();
        reject(new Error("User not logged in"));
      }
      // If user is undefined, keep waiting for the auth state to change
    });
  });
}
export function isNoButton(el: null | EventTarget | HTMLElement): boolean {
  if (!el) {
    return true;
  }
  let target = el as HTMLElement;
  while (target && target !== document.body) {
    if (target.tagName === "BUTTON") {
      return false;
    }
    target = target.parentNode as HTMLElement;
  }
  return true;
}

export function getWeekNumber(date = new Date()) {
  // Copy date so the original isn't modified
  const targetDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );

  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  const dayNum = targetDate.getUTCDay() || 7;
  targetDate.setUTCDate(targetDate.getUTCDate() + 4 - dayNum);

  // Calculate the first day of the year
  const yearStart = new Date(
    Date.UTC(targetDate.getUTCFullYear(), 0, 1),
  ).getTime();

  // Calculate the full weeks between the first day of the year and the current date
  const weekNumber = Math.ceil(
    ((targetDate.getTime() - yearStart) / 86400000 + 1) / 7,
  );

  return weekNumber;
}

export function toCapitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
